import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import { hapticTick } from '../../../../../_utils/haptics'
import useSites, { groupSites } from '../../../../multiLocation/useSites'
import { ChannelIcon } from '../../plans/components/PlanCard'
import { CHANNEL_NAMES } from '../../plans/data/planner'
import { SparkIcon, PlusIcon } from '../../social/components/icons'
import { listCampaigns, saveCampaign, removeCampaign, subscribeCampaigns, hydrateCampaigns, statusOf, tabOf, duplicateCampaign, campaignOf } from '../data/campaignStore'
import { marketingApiService } from '../../../services/marketingApiService'
import { useServerStatuses } from '../data/serverStatus'
import { NEW_PATH, campaignPath } from '../data/open'
import { CopySheet } from './Sheets'
import { usePlans } from '../../plans/usePlans'
import { kindLabel } from '../../plans/components/PlanCard'
import { useMarketingKit } from '../useMarketingKit'
import { planPath } from '../data/open'
import { PostThumb } from '../studio/Previews'
import '../studio/Studio.css'
import '../../plans/Plans.css'
import '../Campaigns.css'

const MANY = { post: 'posts', story: 'Stories', email: 'emails', sms: 'texts' }
const TABS = [
  { id: 'all', label: 'All' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'draft', label: 'Drafts' },
  { id: 'sent', label: 'Sent' },
]
const range = (c) => {
  if (!c.start) return ''
  const a = moment(c.start); const b = moment(c.end || c.start)
  return a.isSame(b, 'day') ? a.format('ddd D MMM') : `${a.format('ddd D MMM')} to ${b.format('ddd D MMM')}`
}

// Emails and texts saved as drafts on their own (not part of a campaign
// here): Bailey's drafts from the chat, and ones saved from the email or text
// editor. QA (2026-10-04): Campaigns opens on this tab, which only listed
// campaigns, so the four drafts Bailey made at Marlowe Hair were nowhere to
// be seen unless you knew to look under Emails or Texts. The server's list is
// already scoped to the person and this location; newest first.
export const looseDrafts = (rows, groups = []) => (Array.isArray(rows) ? rows : [])
  .filter(c => c && c.id && c.status === 'draft' && !c.groupId && !campaignOf(c.id, groups))
  .filter(c => c.type === 'email' || c.type === 'sms')

const LOOSE_LIMIT = 50
const isFromBailey = (c) => /^bailey:chat:/.test(String((c && c.source) || ''))
const AUDIENCE = { all: 'All clients', vip: 'VIP clients', inactive: 'Inactive clients', new: 'New clients', overdue: 'Overdue regulars', custom: 'Hand-picked clients' }
const audienceOf = (c) => {
  const name = AUDIENCE[c.audience] || (c.audience && String(c.audience).startsWith('cat:') ? `Booked ${c.audience.slice(4)}` : '')
  if (!name) return ''
  return typeof c.audienceSize === 'number' ? `${name} (${c.audienceSize.toLocaleString()})` : name
}

// Marketing › Campaigns › Campaigns: every campaign, its status and where
// it's up to. Each opens in the Campaign Studio at its own address; emails
// and texts stay on their own tabs, exactly as before.
const CampaignsList = ({ dispatch, managed = false, openGroupId = null, startSignal = null, onOpenConsumed, onOpenDraft = null }) => {
  const history = useHistory()
  const k = useMarketingKit()
  const { plans, templates, loading } = usePlans({ dispatch, user: k.user, trade: k.trade, salonName: k.salonName, brandColour: k.brandColour, family: k.family, country: k.country, links: k.links, currency: k.currency, enabled: !managed })
  const [list, setList] = useState(listCampaigns)
  const [tab, setTab] = useState('all')
  const [copying, setCopying] = useState(null)
  const [toast, setToast] = useState('')
  const { sites, active, inGroup, isOwnerHere, group: g } = useSites()
  useEffect(() => subscribeCampaigns(() => setList(listCampaigns())), [])
  // The server's list (other devices, other staff); the cache shows meanwhile
  useEffect(() => { hydrateCampaigns() }, [])
  // Drafts saved on their own, from the same list the Emails and Texts tabs read
  const [serverDrafts, setServerDrafts] = useState([])
  useEffect(() => {
    let alive = true
    marketingApiService.getCampaignsPaged(dispatch, { status: 'draft', currentPage: 1, limit: LOOSE_LIMIT })
      .then((res) => { if (alive) setServerDrafts((res && res.campaigns) || []) })
      .catch(() => { if (alive) setServerDrafts([]) })
    return () => { alive = false }
  }, [dispatch])
  const loose = useMemo(() => looseDrafts(serverDrafts, list), [serverDrafts, list])
  // The locations in this group: a stylist at two separate salons has two
  // sites but no group, and nothing is copied between those
  const group = useMemo(() => groupSites({ sites, group: g }), [sites, g])
  // Only owners and managers of a group copy between its locations
  const canCopy = !managed && inGroup && isOwnerHere && group.length > 1

  // Arriving with a campaign to open, or to start a new one
  useEffect(() => {
    if (!openGroupId) return
    if (onOpenConsumed) onOpenConsumed()
    history.push(campaignPath(openGroupId))
  }, [openGroupId]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!startSignal || managed) return
    if (onOpenConsumed) onOpenConsumed()
    history.push(NEW_PATH)
  }, [startSignal]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!toast) return undefined
    const t = setTimeout(() => setToast(''), 5000)
    return () => clearTimeout(t)
  }, [toast])

  const now = new Date()
  // What really happened to each campaign's emails and texts
  const server = useServerStatuses(list.filter(c => c.status === 'approved'))
  const counts = useMemo(() => {
    const out = { all: list.length + loose.length, scheduled: 0, draft: loose.length, sent: 0 }
    list.forEach((c) => { out[tabOf(c, now, server)] += 1 })
    return out
  }, [list, server, loose]) // eslint-disable-line react-hooks/exhaustive-deps
  const looseShown = tab === 'all' || tab === 'draft' ? loose : []
  const shown = list.filter(c => tab === 'all' || tabOf(c, now, server) === tab)
    .sort((a, b) => (a.start || '').localeCompare(b.start || '') * (tab === 'sent' ? -1 : 1))

  const open = (c) => { hapticTick(); history.push(campaignPath(c.id)) }
  const startNew = () => { hapticTick(); history.push(NEW_PATH) }
  const remove = (c) => { removeCampaign(c.id); setToast(`“${c.name}” deleted.`) }

  return (
    <div className="cmg">
      <div className="cmg-bar">
        <div className="cmp-filter" role="tablist" aria-label="Status filter">
          {TABS.map(t => (
            <button key={t.id} role="tab" aria-selected={tab === t.id} className={`cmp-filter-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
              {t.label}<span className="cmp-filter-count">{counts[t.id]}</span>
            </button>
          ))}
        </div>
        {!managed && <button type="button" className="cmp-new-btn" onClick={startNew}><PlusIcon />New campaign</button>}
      </div>

      {!!looseShown.length && (
        <div className="cmg-list" style={{ marginBottom: shown.length ? 12 : 0 }}>
          {looseShown.map((c) => {
            const isText = c.type === 'sms'
            const words = isText ? (c.message || '') : (c.subject || c.message || '')
            const canOpen = !managed && !!onOpenDraft
            return (
              <article key={`loose-${c.id}`} className="cmg-card">
                <button type="button" className="cmg-card-main" disabled={!canOpen} onClick={() => { if (canOpen) { hapticTick(); onOpenDraft(c) } }}>
                  <span className="cmg-card-top">
                    <b>{c.name || (isText ? 'Text' : 'Email')}</b>
                    <span className="cmg-pill is-draft">{isFromBailey(c) ? 'Draft · from Bailey' : 'Draft'}</span>
                  </span>
                  <span className="cmg-card-meta">{words.length > 90 ? `${words.slice(0, 90)}…` : words}</span>
                  <span className="cmg-card-channels">
                    <span className={`cmg-ch is-${c.type}`}><ChannelIcon channel={c.type} size={12} />{isText ? '1 text' : '1 email'}</span>
                    {audienceOf(c) && <span className="cmg-ch">{audienceOf(c)}</span>}
                  </span>
                </button>
              </article>
            )
          })}
        </div>
      )}

      {!shown.length && looseShown.length ? null : !shown.length ? (
        <div className="cmp-empty">
          <div className="cmp-empty-icon"><SparkIcon size={22} /></div>
          <h3 className="cmp-empty-title">{tab === 'all' ? 'Your first campaign' : 'Nothing here yet'}</h3>
          <p className="cmp-empty-text">
            {managed ? 'Your salon runs your marketing, so campaigns are made there.'
              : tab === 'all' ? 'Posts, Stories, texts and emails together, from a day to six weeks. Start from nothing, or from one of Bailey’s ideas for the seasons ahead.'
                : 'Campaigns show here once they get this far.'}
          </p>
          {!managed && tab === 'all' && <button type="button" className="cmp-new-btn" onClick={startNew}><PlusIcon />New campaign</button>}
          {!managed && tab === 'all' && (() => {
            const ideas = [...plans.filter(p => p.items && p.items.length), ...templates.filter(t => !plans.some(p => p.key === t.key))].slice(0, 3)
            if (loading && !ideas.length) return <p className="cst-muted" style={{ marginTop: 20 }}>Bailey is looking for ideas…</p>
            if (!ideas.length) return null
            return (
              <div style={{ width: '100%', maxWidth: 900, marginTop: 28, textAlign: 'left' }}>
                <span className="mkp-label">Bailey’s ideas for you</span>
                <div className="cst-ideas">
                  {ideas.map((p) => {
                    const art = p.items.filter(i => i.on && i.post).slice(0, 2)
                    return (
                      <button key={p.key} type="button" className="cst-idea" onClick={() => { hapticTick(); history.push(planPath(p), { from: '/marketing-campaigns' }) }}>
                        <span className="cst-idea-art" aria-hidden>
                          {art.map(i => <span key={i.id} className="cst-idea-post"><PostThumb post={i.post} kit={k.kit} width={i.post.format === 'story' ? 52 : 72} /></span>)}
                        </span>
                        <span className="cst-idea-text">
                          <span className="mkp-kind">{kindLabel(p)}</span>
                          <b>{p.title}</b>
                          <small>{range(p)}</small>
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })()}
        </div>
      ) : (
        <div className="cmg-list">
          {shown.map((c) => {
            const st = statusOf(c, now, server)
            const on = c.items.filter(i => i.on || i.cancelled)
            const channels = ['post', 'story', 'email', 'sms'].map(ch => ({ ch, n: on.filter(i => i.channel === ch).length })).filter(x => x.n)
            return (
              <article key={c.id} className="cmg-card">
                <button type="button" className="cmg-card-main" onClick={() => open(c)}>
                  <span className="cmg-card-top">
                    <b>{c.name}</b>
                    <span className={`cmg-pill is-${st.id}`}>{st.label}</span>
                  </span>
                  <span className="cmg-card-meta">{range(c)}{c.copiedFrom ? ` · Copied from ${c.copiedFrom.salonName || 'another location'}` : c.planKey ? ' · From Bailey' : ''}</span>
                  <span className="cmg-card-channels">
                    {channels.length ? channels.map(x => (
                      <span key={x.ch} className={`cmg-ch is-${x.ch}`}><ChannelIcon channel={x.ch} size={12} />{x.n} {x.n === 1 ? (x.ch === 'story' ? 'Story' : CHANNEL_NAMES[x.ch].toLowerCase()) : MANY[x.ch]}</span>
                    )) : <span className="cmg-ch">Nothing in it yet</span>}
                  </span>
                </button>
                {!managed && (
                  <div className="cmg-card-actions">
                    {canCopy && <button type="button" className="mkp-link is-quiet" onClick={() => setCopying(c)}>Copy to locations</button>}
                    <button type="button" className="mkp-link is-quiet" onClick={() => { const d = saveCampaign(duplicateCampaign(c)); open(d) }}>Duplicate</button>
                    {c.status !== 'approved' && <button type="button" className="mkp-link is-quiet" onClick={() => remove(c)}>Delete</button>}
                  </div>
                )}
              </article>
            )
          })}
        </div>
      )}

      {copying && (
        <CopySheet
          campaign={copying}
          sites={group}
          active={active}
          onDone={(chosen) => { setCopying(null); setToast(`A draft is waiting at ${chosen.map(s => s.salonName).join(', ').replace(/, ([^,]*)$/, ' and $1')}. Switch to each location to check and approve it.`) }}
          onClose={() => setCopying(null)}
        />
      )}
      {toast && <div className="sx-toast" role="status">{toast}</div>}
    </div>
  )
}

export default CampaignsList
